import { AuthKitProvider, useAuth as useAuthKit } from '@workos-inc/authkit-react';
import { type ReactNode } from 'react';

const CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined;
const REDIRECT_URI = import.meta.env.VITE_WORKOS_REDIRECT_URI as string | undefined;

// The DEFAULT org (MineTwin's customer org). No longer force-pinned — org
// selection/scoping lives in contexts/OrgContext.tsx (MULTI-ORG-PORTAL plan).
// Still used as: the staff Entra sign-in scope on the landing page, and the
// safety fallback when membership listing is unavailable (which reproduces
// the old single-org behavior exactly).
export const EXPECTED_ORG_ID =
  (import.meta.env.VITE_WORKOS_ORG_ID as string | undefined) ??
  'org_01KWJF5AT8N0YRT219MRH1FM6T';

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!CLIENT_ID) {
    throw new Error(
      'VITE_WORKOS_CLIENT_ID is missing. Add it to .env.local (see .env.example).',
    );
  }
  return (
    <AuthKitProvider
      clientId={CLIENT_ID}
      redirectUri={REDIRECT_URI}
      // devMode={true} is intentional in PROD — not just dev.
      //
      // authkit-js 0.20.x switched its production storage mode to
      // memoryStorage + WorkOS session cookies (devMode=false →
      // `useCookie` getter returns true; tokens live in memory + the
      // refresh path round-trips a WorkOS-set HTTP-only cookie). That
      // pattern requires a backend that can mint cookies on the right
      // domain — which a pure static SWA cannot do. With devMode=false
      // here, every page refresh wipes memoryStorage, the cookie path
      // has no cookie to refresh from, and the user is bounced to the
      // hosted login page. Reported by Cloud Lead 2026-06-20 after the
      // authkit-react 0.4.1 → 0.16.1 bump went out.
      //
      // Forcing devMode=true switches the underlying authkit-js back
      // to localStorage-backed session persistence, which is the only
      // option that actually works for an SPA without a backend.
      // Standard XSS-mitigation caveats apply; the CSP and the
      // X-Content-Type-Options / Referrer-Policy headers in
      // public/staticwebapp.config.json are our defense.
      devMode={true}
      // The WorkOS session itself died (refresh token rejected, not a network
      // blip: AuthKit only calls this on a definitive RefreshError, and never
      // during initial load). authkit-react keeps `user` set in that state,
      // so without this the portal stays mounted with every SpacetimeDB
      // token request rejecting and every table empty until the user reloads
      // (#174). Re-entering sign-in is what that reload did by hand; with the
      // WorkOS session cookie still alive it round-trips without a prompt.
      onRefreshFailure={({ signIn }) => void signIn()}
    >
      {children}
    </AuthKitProvider>
  );
}

export { useAuthKit };

/**
 * Sign-out hook. Returns a callback that delegates to AuthKit's built-in
 * `signOut({ returnTo })`, which:
 *
 *   1. Reads the `sid` claim from the cached access token.
 *   2. Hits
 *      `https://api.workos.com/user_management/sessions/logout?session_id=<sid>&return_to=<here>`
 *      — clears the WorkOS HTTP-only session cookie server-side.
 *   3. Removes local AuthKit storage (refresh-token, etc.).
 *   4. `window.location.assign(returnTo)` so the user lands back on this
 *      portal's landing page, which renders the Login button.
 *
 * Why not the previous hand-built URL: WorkOS's logout endpoint requires
 * BOTH `session_id` and `return_to`. Sending only `return_to` returned
 * `session_id_string_required: Session Id must be a non-empty string.`
 * (Cloud Lead 2026-06-28). Letting AuthKit assemble the URL sidesteps
 * the omission.
 *
 * Why same-origin `returnTo`: the portal serves its own LandingPage at
 * `/` when unauthenticated — that's where the user should land after
 * sign-out. Using `window.location.origin` needs no per-deploy code change: it
 * sends the matching `returnTo` from localhost, portal.minetwin.ai and any
 * future deploy, but WorkOS only honours it once that origin is on the
 * allowlist (Prereq below: localhost is not on it today).
 *
 * Prereq: the resolved `returnTo` must be on the "Logout redirect URIs"
 * allowlist (Redirects → Logout) of the WorkOS application the portal signs
 * in with. If not, WorkOS falls through to that application's default
 * sign-out URI — the cookie still clears but the UX lands on the wrong site.
 * Today the portal signs in with the web app, whose default sign-out URI is
 * `https://minetwin.turbotwin.cloud`, and the only sign-out URI registered
 * for the portal is `https://portal.minetwin.ai`. `http://localhost:5173`
 * has none, so a local sign-out lands on that default too. Once the portal
 * has its own WorkOS application, its sign-out URIs are set per
 * `docs/PORTAL-PLAYBOOK.md` §7 (step E.3).
 * WorkOS does an exact-string match on the allowlist, so the trailing slash
 * matters: we send the bare origin (no slash) to match how URLs are stored
 * in the dashboard (`https://portal.minetwin.ai`). Browsers still resolve a
 * bare-origin URL to the homepage, so functionally identical to a
 * trailing-slash version (Cloud Lead 2026-06-29).
 */
export function useLocalSignOut(): () => void {
  const { signOut } = useAuthKit();
  return () => {
    signOut({ returnTo: window.location.origin });
  };
}
